import { motion } from "framer-motion";

const INK = "#1F2A44";
const RED = "#E4572E";
const BLUE = "#2B59C3";

const draw = (i = 0) => ({
  initial: { pathLength: 0, opacity: 0 },
  whileInView: { pathLength: 1, opacity: 1 },
  viewport: { once: true, amount: 0.1 },
  transition: { duration: 1.1, delay: 0.15 * i, ease: [0.65, 0, 0.35, 1] },
});

export const P = ({ d, i = 0, c = INK, w = 2.4, fill = "none" }) => (
  <motion.path d={d} stroke={c} strokeWidth={w} fill={fill} strokeLinecap="round" strokeLinejoin="round" {...draw(i)} />
);

const T = ({ x, y, children, s = 18, c = INK, a = "middle" }) => (
  <text x={x} y={y} fontSize={s} fill={c} textAnchor={a} fontFamily="Kalam, cursive">{children}</text>
);

const Arrow = ({ x1, y1, x2, y2, i = 0, c = INK }) => {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const h = (o) => `${x2 - 10 * Math.cos(ang + o)},${y2 - 10 * Math.sin(ang + o)}`;
  return <P d={`M${x1},${y1} Q${(x1 + x2) / 2},${(y1 + y2) / 2 - 12} ${x2},${y2} M${h(0.5)} L${x2},${y2} L${h(-0.5)}`} i={i} c={c} />;
};

const Box = ({ x, y, w, h, i = 0, c = INK }) => (
  <P d={`M${x + 2},${y} L${x + w},${y + 2} L${x + w - 1},${y + h} L${x},${y + h - 2} Z`} i={i} c={c} />
);

const sketches = {
  spendToRevenue: () => (
    <>
      <Box x={20} y={70} w={110} h={80} /><T x={75} y={105}>Ads</T><T x={75} y={132} c={RED}>₹1 lakh</T>
      <Arrow x1={140} y1={110} x2={250} y2={110} i={2} />
      <T x={195} y={85} s={15}>right metrics</T>
      <Box x={262} y={50} w={120} h={120} i={3} c={BLUE} /><T x={322} y={95}>Revenue</T>
      <T x={322} y={130} s={26} c={BLUE}>₹3–4L</T>
      <P d="M280,145 Q322,160 365,143" i={5} c={RED} />
      <T x={200} y={215} s={16}>cost ↓  revenue ↑  = Performance Marketing</T>
    </>
  ),
  uspTshirts: () => (
    <>
      {[60, 250].map((x, k) => (
        <g key={x}>
          <P d={`M${x},60 l30,-20 h30 q10,14 30,0 h30 l30,20 l-18,26 l-18,-10 v100 h-88 v-100 l-18,10 Z`} i={k * 2} c={k ? BLUE : INK} />
          <T x={x + 75} y={210} c={k ? BLUE : INK}>{k ? "Brand B" : "Brand A"}</T>
          <T x={x + 75} y={232} s={14}>{k ? "keeps shape after wash" : "just ₹799"}</T>
        </g>
      ))}
      <P d="M285,100 q40,-30 80,0 q-40,40 -80,0" i={4} c={RED} />
      <T x={200} y={30} s={16} c={RED}>USP = why YOU?</T>
    </>
  ),
  radius: () => (
    <>
      <P d="M200,120 m-100,0 a100,90 0 1,0 200,0 a100,90 0 1,0 -200,0" i={0} c={BLUE} />
      <P d="M200,120 m-50,0 a50,45 0 1,0 100,0 a50,45 0 1,0 -100,0" i={1} />
      <P d="M190,112 h20 v18 h-20 Z M190,112 l10,-10 l10,10" i={2} c={RED} />
      <T x={200} y={152} s={14}>cafe</T>
      <Arrow x1={200} y1={120} x2={298} y2={95} i={3} c={RED} />
      <T x={340} y={85} s={16} c={RED}>4–5 km</T>
      {[[120, 70], [270, 160], [150, 175], [255, 60]].map(([x, y], k) => (
        <P key={k} d={`M${x},${y} m-6,0 a6,6 0 1,0 12,0 a6,6 0 1,0 -12,0`} i={4 + k * 0.3} />
      ))}
      <T x={60} y={225} s={14} a="start">dots = your real customers</T>
    </>
  ),
  funnelNumbers: () => (
    <>
      {[["Impressions", "1,00,000"], ["Clicks", "1,000"], ["Add to cart", "100"], ["Purchases", "25"]].map(([a, b], k) => (
        <g key={a}>
          <P d={`M${40 + k * 30},${30 + k * 48} H${360 - k * 30} L${350 - k * 30},${70 + k * 48} H${50 + k * 30} Z`} i={k} c={k === 3 ? RED : INK} />
          <T x={200} y={57 + k * 48} s={16}>{`${a} · ${b}`}</T>
        </g>
      ))}
      <T x={370} y={230} s={14} a="end" c={BLUE}>CTR 1% · CVR 2.5%</T>
    </>
  ),
  ltvStack: () => (
    <>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
        <P key={k} d={`M${40 + k * 42},200 v-${30 + k * 18} h30 v${30 + k * 18}`} i={k * 0.4} c={k === 0 ? RED : BLUE} />
      ))}
      <P d="M30,200 H380" i={0} />
      <T x={55} y={225} s={14} c={RED}>1st order</T>
      <T x={320} y={40} s={16} c={BLUE}>LTV = all orders</T>
      <P d="M60,140 Q200,60 360,20" i={4} c={RED} w={1.6} />
    </>
  ),
  metaTree: () => (
    <>
      <Box x={140} y={15} w={120} h={42} /><T x={200} y={43}>Campaign</T>
      {[30, 150, 270].map((x, k) => (
        <g key={x}>
          <Arrow x1={200} y1={60} x2={x + 50} y2={100} i={1 + k * 0.3} />
          <Box x={x} y={105} w={100} h={38} i={2} c={BLUE} /><T x={x + 50} y={130} s={16} c={BLUE}>Ad set</T>
          {[0, 1].map((j) => (
            <g key={j}>
              <P d={`M${x + 50},146 L${x + 22 + j * 56},180`} i={3} />
              <Box x={x + 2 + j * 52} y={184} w={44} h={34} i={3.5} c={RED} />
              <T x={x + 24 + j * 52} y={207} s={13} c={RED}>Ad</T>
            </g>
          ))}
        </g>
      ))}
    </>
  ),
  serp: () => (
    <>
      <Box x={30} y={15} w={340} h={36} /><T x={50} y={40} s={15} a="start">best running shoes under 3000</T>
      {[0, 1].map((k) => (
        <g key={k}>
          <Box x={30} y={66 + k * 44} w={340} h={36} i={1 + k} c={BLUE} />
          <T x={50} y={90 + k * 44} s={14} a="start" c={BLUE}>Sponsored · your ad</T>
        </g>
      ))}
      {[0, 1].map((k) => (
        <P key={k} d={`M40,${172 + k * 28} H${330 - k * 60}`} i={3 + k} />
      ))}
      <T x={365} y={190} s={14} a="end">organic</T>
      <Arrow x1={385} y1={30} x2={375} y2={78} i={4} c={RED} />
    </>
  ),
  trackingFlow: () => (
    <>
      {[["Ad click", 20], ["Product", 115], ["Cart", 210], ["Purchase", 305]].map(([t, x], k) => (
        <g key={t}>
          <Box x={x} y={40} w={80} h={44} i={k} c={k === 3 ? RED : INK} />
          <T x={x + 40} y={68} s={15}>{t}</T>
          {k < 3 && <Arrow x1={x + 82} y1={62} x2={x + 112} y2={62} i={k + 0.5} />}
          <P d={`M${x + 40},88 V140`} i={k + 1} c={BLUE} w={1.6} />
        </g>
      ))}
      <Box x={60} y={145} w={130} h={40} i={4} c={BLUE} /><T x={125} y={171} s={15} c={BLUE}>Pixel (browser)</T>
      <Box x={215} y={145} w={130} h={40} i={4.5} c={BLUE} /><T x={280} y={171} s={15} c={BLUE}>CAPI (server)</T>
      <T x={200} y={222} s={15}>→ Meta / GA4 learn who buys</T>
    </>
  ),
  hookTimeline: () => (
    <>
      <P d="M30,120 H380" i={0} />
      <P d="M30,95 h90 v50 h-90 Z" i={1} c={RED} fill="rgba(228,87,46,0.08)" />
      <T x={75} y={85} s={15} c={RED}>0–3s HOOK</T>
      <T x={180} y={110} s={15}>problem · proof</T>
      <T x={330} y={110} s={15}>CTA</T>
      {[30, 120, 260, 380].map((x, k) => <P key={x} d={`M${x},112 V128`} i={2 + k * 0.2} />)}
      <T x={30} y={150} s={13} a="start">0s</T><T x={120} y={150} s={13}>3s</T><T x={380} y={150} s={13} a="end">15s</T>
      <P d="M40,200 Q90,170 120,200 T200,200" i={3} c={BLUE} />
      <T x={300} y={205} s={15} c={BLUE}>thumb stops here</T>
    </>
  ),
  wireframe: () => (
    <>
      <P d="M130,10 h140 a10,10 0 0 1 10,10 v200 a10,10 0 0 1 -10,10 h-140 a10,10 0 0 1 -10,-10 v-200 a10,10 0 0 1 10,-10" i={0} />
      <P d="M140,35 H260 M140,50 H230" i={1} />
      <Box x={140} y={62} w={120} h={60} i={2} c={BLUE} />
      <T x={200} y={98} s={13} c={BLUE}>product</T>
      <P d="M140,135 h10 M155,135 h10 M170,135 h10 M185,135 h10 M200,135 h10" i={3} c="#D4A017" />
      <P d="M140,195 h120 v22 h-120 Z" i={4} c={RED} fill="rgba(228,87,46,0.12)" />
      <T x={200} y={211} s={13} c={RED}>Buy now</T>
      <T x={60} y={45} s={14}>headline =</T><T x={60} y={63} s={14}>ad promise</T>
      <Arrow x1={95} y1={70} x2={135} y2={45} i={5} c={RED} />
      <T x={340} y={140} s={14}>reviews ★</T>
      <Arrow x1={310} y1={140} x2={262} y2={135} i={5} c={RED} />
      <T x={345} y={205} s={14}>sticky CTA</T>
    </>
  ),
  retargetLoop: () => (
    <>
      <P d="M200,40 a110,80 0 1,1 -1,0" i={0} c={BLUE} />
      {[["Visit", 200, 30], ["No buy", 320, 120], ["Reminder ad", 200, 215], ["Sale!", 80, 120]].map(([t, x, y], k) => (
        <g key={t}>
          <P d={`M${x - 42},${y - 16} h84 v30 h-84 Z`} i={1 + k} fill="#FFFDF6" c={k === 3 ? RED : INK} />
          <T x={x} y={y + 5} s={15} c={k === 3 ? RED : INK}>{t}</T>
        </g>
      ))}
      <T x={200} y={128} s={15}>7–30 day window</T>
    </>
  ),
  aboCbo: () => (
    <>
      <T x={100} y={25} s={18}>ABO</T><T x={300} y={25} s={18} c={BLUE}>CBO</T>
      <P d="M200,15 V225" i={0} w={1.2} />
      {[0, 1, 2].map((k) => (
        <g key={k}>
          <P d={`M${40 + k * 45},${200 - 70} v70 h30 v-70 Z`} i={1 + k * 0.3} />
          <T x={55 + k * 45} y={120} s={13}>₹1k</T>
        </g>
      ))}
      <Box x={255} y={40} w={90} h={36} i={2} c={BLUE} /><T x={300} y={64} s={14} c={BLUE}>₹3k pool</T>
      {[[230, 40], [275, 110], [320, 20]].map(([x, h], k) => (
        <g key={k}>
          <Arrow x1={300} y1={78} x2={x + 15} y2={198 - h} i={3 + k * 0.3} c={BLUE} />
          <P d={`M${x},200 v-${h} h30 v${h} Z`} i={3.5} c={BLUE} />
        </g>
      ))}
      <T x={300} y={225} s={13}>money flows to winners</T>
    </>
  ),
  scaleGraph: () => (
    <>
      <P d="M40,20 V200 H380" i={0} />
      <P d="M40,190 L110,170 L180,145 L250,118 L320,90 L370,72" i={1} c={BLUE} />
      <P d="M40,190 L120,180 L150,60 L190,170 L260,160" i={2} c={RED} />
      <T x={330} y={60} s={14} c={BLUE}>+20% steps</T>
      <T x={190} y={55} s={14} c={RED}>budget shock</T>
      <T x={370} y={222} s={13} a="end">days →</T>
    </>
  ),
  funnel: () => (
    <>
      <P d="M40,20 H360 L280,95 H120 Z" i={0} />
      <P d="M120,100 H280 L245,160 H155 Z" i={1} c={BLUE} />
      <P d="M155,165 H245 L222,220 H178 Z" i={2} c={RED} />
      <T x={200} y={62}>TOFU · cold</T>
      <T x={200} y={136} s={16} c={BLUE}>MOFU · warm</T>
      <T x={200} y={198} s={14} c={RED}>BOFU</T>
    </>
  ),
};

export const Sketch = ({ name, caption }) => {
  const S = sketches[name];
  if (!S) return null;
  return (
    <figure className="sketch" data-testid={`sketch-${name}`}>
      <svg viewBox="0 0 400 240" role="img" aria-label={caption || name} filter="url(#pencil)">
        <S />
      </svg>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

export const PencilFilter = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
    <filter id="pencil">
      <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="2" result="n" />
      <feDisplacementMap in="SourceGraphic" in2="n" scale="2.2" />
    </filter>
  </svg>
);
